// React
import React, { useEffect, useState } from "react";
// MUI
import { Card, Stack, Button, Container, Typography, Grid, Toolbar, Box } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Components
import GoTable from "../../../common/GoTable/GoTable";
import GoDrawer from "../../../components/Inputs/GoDrawer";
// Constants
import { JOURNAL_TABLE_HEADS } from "../../../configs/TableHeaderConstants";
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
import PermissionConstants from "../../../configs/PermissionConstants";
// Services
import { get, del, gen_link } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
// Hooks
import usePermissions from "../../../hooks/usePermissions";
// Utils
import { filterConfig } from "./Journal.utils";
//Third party
import { Form, Formik } from "formik";
import GoForm from "../../../components/Inputs/GoForm";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
import { styled } from "@mui/material/styles";
// components
import Invoice from "../Invoices/Invoice";
import ReceiptHeader from "../Receipts/ReceiptHeader";
import Voucher from "../Vouchers/Voucher";
import CreditNote from "../CreditNotes/CreditNote";
import DebitNote from "../DebitNotes/DebitNote";
import { toast } from "react-hot-toast";
import GoDialog from "../../../common/GoDialog/GoDialog";

const StyledRoot = styled("div")(({ theme }) => ({
    // minHeight: 96,
    // width: "100%",
    display: "flex",
    // justifyContent: "space-between",
    padding: theme.spacing(2, 2, 2, 2),
    paddingBottom: '10px'
}));

const voucherTypes = ["Payment Voucher", "Journal Voucher", "Prepayment Voucher", "PDC Bank Payment"];

const Journals = () => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [formFields, setFormFields] = useState(filterConfig.formFields);
    const [open, setOpen] = useState(false);
    const [id, setId] = useState(undefined);
    const [filter, setFilter] = useState({
        sort: "id",
        order: "asc",
        max: 10,
        offset: 0,
        search: ""
    });
    const [rowSelected, setRowSelected] = useState(null);
    const [confirm, setConfirm] = useState(false);
    const [journals, setJournals] = useState([]);

    const attachOptions = (name, options) => {
        setFormFields(prevFormFields =>
            prevFormFields.map(field =>
                field.name === name
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const handleSoaAccounts = async () => {
        spinService.on();
        const response = await get(EntityConstants.SOA_ACCOUNTS);
        const data = await response.json();
        attachOptions("accounts", data);
        spinService.off();
    }

    // const handleJournalsChange = async () => {
    //     spinService.on();
    //     const response = await get(EntityConstants.JOURNALS, `sort=${filter.sort}&order=${filter.order}&max=${filter.max}&offset=${filter.offset + 1}&search=${filter.search}`);
    //     const data = await response.json();
    //     setJournals(data);
    //     spinService.off();
    // };

    const handleSubmit = async (values) => {
        spinService.on();
        const response = await get(EntityConstants.JOURNALS, `accounts=${values.accounts.join(',')}&start_date=${values.start_date}&end_date=${values.end_date}`);
        const data = await response.json();
        setJournals(data);
        spinService.off();
    }

    const handlePdfDownloadChange = async (values) => {
        const response = await fetch(gen_link(`${EntityConstants.SOA_DOWNLOAD}`, `accounts=${values.accounts.join(',')}&start_date=${values.start_date}&end_date=${values.end_date}`));
        const pdfBlob = await response.blob();
        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
        const previewWindow = window.open(pdfBlobUrl, '_blank');

        // const link = document.createElement('a');
        // link.href = gen_link(`${EntityConstants.SOA_DOWNLOAD}`, `accounts=${values.accounts.join(',')}&start_date=${values.start_date}&end_date=${values.end_date}`);
        // link.target = '_blank';
        // link.click();
    };

    const onAction = (item, action) => {
        if (action === "edit") {
            setRowSelected(item);
            setOpen(true);
        }
        if (action === "delete") {
            if (["Invoice Line", "Receipt Line"].indexOf(item.source) > -1) {
                toast.error(`Delete action not applicable for ${item.source}`)
            } else {
                setRowSelected(item);
                setConfirm(true);
            }
        }
    }

    const onClose = () => {
        setRowSelected(null);
        setOpen(false);
    };

    const getByTitle = (source) => {
        switch (source) {
            case 'Invoice Line':
                return "Invoice";
            case 'Receipt Line':
                return "Receipt";
            default:
                return source;
        }
    }

    const handleClose = async (res) => {
        if (res) {
            spinService.on();
            let entity = voucherTypes.indexOf(rowSelected.source) > -1 ? EntityConstants.VOUCHER_LINES : rowSelected.source === "Credit Note" ? EntityConstants.CREDIT_NOTE_LINES : EntityConstants.DEBIT_NOTE_LINES;
            let message = voucherTypes.indexOf(rowSelected.source) > -1 ? MessageContants.VOUCHER_LINE_DELETED : rowSelected.source === "Credit Note" ? MessageContants.CREDIT_NOTE_LINE_DELETED : MessageContants.DEBIT_NOTE_LINE_DELETED;
            const response = await del(entity + `/${rowSelected.source_id}`);
            if (response.status === 200) {
                handleSubmit(filterConfig.state);
                toast.success(message);
            }
            spinService.off();
        } else {
            setRowSelected(null);
        }
        setConfirm(false);
    };

    useEffect(() => {
        // handleJournalsChange();
        handleSoaAccounts();
    }, [filter]);

    return (
        <React.Fragment>
            <Container maxWidth={false}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                >
                    <Typography variant="h4" gutterBottom>
                        STATEMENT OF ACCOUNT
                    </Typography>
                </Stack>
                <Card>
                    <StyledRoot>
                        <Formik
                            enableReinitialize
                            initialValues={filterConfig.state}
                            validationSchema={filterConfig.schema}
                            onSubmit={(values) => handleSubmit(values)}
                        >
                            {({
                                values,
                                touched,
                                errors,
                                handleChange,
                                handleSubmit,
                            }) => (
                                <Form noValidate onSubmit={handleSubmit}>
                                    <Grid
                                        container
                                        justifyContent="space-between"
                                        alignItems="center"  // Vertical alignment
                                        rowSpacing={1}
                                        columnSpacing={{ xs: 1, sm: 2, md: 2, lg: 2 }}
                                        style={{ maxWidth: "1000px", margin: "0 auto" }}
                                    >
                                        {formFields.map((formField, index) => (
                                            <Grid key={index} item xs={12} sm={12} md={3} lg={3}>
                                                <GoForm
                                                    config={formField}
                                                    values={values}
                                                    touched={touched}
                                                    errors={errors}
                                                    handleChange={handleChange}
                                                />
                                            </Grid>
                                        ))}
                                        <Grid item xs={12} sm={12} md={2} lg={1}>
                                            {permissions.has(PermissionConstants.SOA_VIEW) && (
                                                <Box sx={{ p: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<Iconify icon="eva:save-fill" />}
                                                        type="submit"
                                                    >
                                                        Filter
                                                    </Button>
                                                </Box>
                                            )}
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={2} lg={1}>
                                            {permissions.has(PermissionConstants.SOA_DOWNLOAD) && (
                                                <Box sx={{ p: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<Iconify icon="typcn:download" />}
                                                        onClick={() => handlePdfDownloadChange(values)}
                                                    >
                                                        Download
                                                    </Button>
                                                </Box>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    </StyledRoot>
                </Card>
                <Card>
                    <GoTable
                        tableHeads={JOURNAL_TABLE_HEADS}
                        tableDatas={journals}
                        totalCount={0}
                        filter={filter}
                        onFilter={(filter) => setFilter(filter)}
                        onView={(row) => { setId(row.id); setOpen(true) }}
                        search={false}
                        pagination={false}
                        onAction={(row, action) => onAction(row, action)}
                    />
                </Card>
            </Container>
            {
                open && rowSelected !== null && <GoDrawer
                    minwidth="50%"
                    title={getByTitle(rowSelected.source)}
                    open={open}
                    onClose={() => onClose()}
                >
                    {rowSelected.source === "Invoice Line" && <Invoice id={rowSelected.parent_source_id} onClose={() => onClose()} />}
                    {rowSelected.source === "Receipt Line" && <ReceiptHeader id={rowSelected.parent_source_id} onClose={() => onClose()} />}
                    {rowSelected.source === "Credit Note" && <CreditNote id={rowSelected.parent_source_id} onClose={() => onClose()} />}
                    {rowSelected.source === "Debit Note" && <DebitNote id={rowSelected.parent_source_id} onClose={() => onClose()} />}
                    {voucherTypes.indexOf(rowSelected.source) > -1 && <Voucher voucherType={rowSelected.source} id={rowSelected.parent_source_id} onClose={() => onClose()} />}

                </GoDrawer>
            }
            <GoDialog
                open={confirm}
                title=""
                content="Are you sure you want to delete?"
                positive="Yes"
                negative="No"
                handleClose={(res) => handleClose(res)}
            />
        </React.Fragment>
    );
};

export default Journals;